import type { FC } from "react";
import { useState, useContext } from "react";
import { useMutation } from "@tanstack/react-query";
import { Authentication } from "@src/core/services";
import { AxiosError } from "axios";
import { enqueueSnackbar } from "notistack";
import { Store } from "@src/core/store";
import {
  NpsContainer,
  NpsTrigger,
  NpsForm,
  StarsContainer,
  StarButton,
  FormContent,
  SubmitButton,
  ThankYouMessage,
  CloseButton,
  FormHeader,
} from "./nps.styles";
import { TextField } from "@mui/material";
import { palette } from "@src/core/styles";

type NpsProps = {
  position?: "bottom-right" | "bottom-left" | "top-right" | "top-left";
};

export const Nps: FC<NpsProps> = ({ position = "bottom-right" }) => {
  const {
    state: {
      authorizeStore: { name },
    },
  } = useContext(Store);

  const isAuthenticated = name.length !== 0;
  const [isOpen, setIsOpen] = useState(false);
  const [selectedStars, setSelectedStars] = useState<number>(0);
  const [message, setMessage] = useState<string>("");
  const [isSubmitted, setIsSubmitted] = useState(false);

  const { mutate, isPending } = useMutation({
    mutationFn: Authentication.nps,
    onSuccess: () => {
      setIsSubmitted(true);
      enqueueSnackbar("Спасибо за обратную связь", {
        variant: "reportComplete",
        className: "success",
        preventDuplicate: true,
      });
      // Скрываем форму через 3 секунды
      setTimeout(() => {
        setIsOpen(false);
        setIsSubmitted(false);
        setSelectedStars(0);
        setMessage("");
      }, 3000);
    },
    onError: (error: AxiosError) => {
      console.error("NPS submission error:", error);
      enqueueSnackbar("Произошла ошибка при отправке", {
        variant: "error",
        preventDuplicate: true,
      });
    },
  });

  const handleStarClick = (star: number) => {
    setSelectedStars(star);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedStars > 0) {
      mutate({
        stars: selectedStars,
        value: message.trim() || undefined,
      });
    }
  };

  const handleTriggerClick = () => {
    if (!isSubmitted) {
      setIsOpen(!isOpen);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setSelectedStars(0);
    setMessage("");
    setIsSubmitted(false);
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <NpsContainer position={position}>
      {!isOpen && !isSubmitted ? (
        <NpsTrigger onClick={handleTriggerClick} title="Оцените наш сервис">
          ⭐
        </NpsTrigger>
      ) : (
        <NpsForm onSubmit={handleSubmit}>
          <FormHeader>
            <CloseButton
              type="button"
              onClick={handleClose}
              aria-label="Закрыть форму"
            >
              ×
            </CloseButton>
          </FormHeader>
          {isSubmitted ? (
            <ThankYouMessage>Спасибо за обратную связь</ThankYouMessage>
          ) : (
            <>
              <FormContent>
                <StarsContainer>
                  {[1, 2, 3, 4, 5].map((star) => (
                    <StarButton
                      key={star}
                      type="button"
                      onClick={() => handleStarClick(star)}
                      selected={selectedStars >= star}
                    >
                      ⭐
                    </StarButton>
                  ))}
                </StarsContainer>
                <TextField
                  multiline
                  rows={3}
                  placeholder="Ваш отзыв (необязательно)"
                  value={message}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value.length <= 250) {
                      setMessage(value);
                    }
                  }}
                  inputProps={{ maxLength: 250 }}
                  helperText={`${message.length}/250`}
                  sx={{
                    width: "100%",
                    marginTop: "16px",
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: palette.colors.outline_variant,
                      },
                      "&:hover fieldset": {
                        borderColor: palette.colors.green_base,
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: palette.colors.green_base,
                      },
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: palette.colors.green_base,
                    },
                  }}
                />
                <SubmitButton
                  type="submit"
                  disabled={selectedStars === 0 || isPending}
                  variant="contained"
                >
                  Отправить
                </SubmitButton>
              </FormContent>
            </>
          )}
        </NpsForm>
      )}
    </NpsContainer>
  );
};

